import type {
  InvoiceItem,
  InvoicesListResponse,
  SubscriptionPlanConfig,
} from "@/src/types/subscriptionPlan.types";

/** Normalize API plan (may send `key` or `planKey`). */
export function normalizePlanFromApi(raw: SubscriptionPlanConfig & { key?: string }): SubscriptionPlanConfig {
  const planKey = raw.planKey ?? raw.key ?? "";
  return {
    planKey,
    name: raw.name,
    tagline: raw.tagline ?? null,
    monthlyPrice: Number(raw.monthlyPrice),
    yearlyPrice: Number(raw.yearlyPrice),
    trialDurationDays: Number(raw.trialDurationDays),
    isTrialPlan: Boolean(raw.isTrialPlan),
    maxProperties: raw.maxProperties ?? null,
    maxAssets: raw.maxAssets ?? null,
    features: Array.isArray(raw.features) ? raw.features : [],
    limits: Array.isArray(raw.limits) ? raw.limits : [],
    isPopular: Boolean(raw.isPopular),
    isActive: raw.isActive !== false,
  };
}

/**
 * GET /subscriptions/invoices returns `data` as InvoiceItem[] with total/page/limit on the envelope.
 * apiClient unwraps to the array only; this normalizes array or { items } shapes.
 */
export function normalizeInvoicesList(raw: unknown): InvoicesListResponse {
  if (Array.isArray(raw)) {
    return {
      items: raw as InvoiceItem[],
      total: raw.length,
      page: 1,
      limit: raw.length || 20,
    };
  }

  if (!raw || typeof raw !== "object") {
    return { items: [], total: 0, page: 1, limit: 20 };
  }

  const r = raw as Record<string, unknown>;
  const items = (
    Array.isArray(r.items)
      ? r.items
      : Array.isArray(r.data)
        ? r.data
        : []
  ) as InvoiceItem[];

  return {
    items,
    total: typeof r.total === "number" ? r.total : items.length,
    page: typeof r.page === "number" ? r.page : 1,
    limit: typeof r.limit === "number" ? r.limit : 20,
  };
}

/** Paths from API may be `/api/subscriptions/...` while axios baseURL already ends with `/api`. */
export function subscriptionApiPath(path: string): string {
  if (path.startsWith("http")) return path;
  let normalized = path;
  if (normalized.startsWith("/api/")) {
    normalized = normalized.slice(4);
  }
  if (!normalized.startsWith("/")) {
    normalized = `/${normalized}`;
  }
  return normalized;
}
